import { state } from "../state.js";
import { audioCtx, getBuffer } from "./context.js";
import { globalSfxVolume } from "./sfx.js";

let vroomSource: any = null;
let vroomGainNode: GainNode | null = null;
// Which skin the current vroom source was built for. Modern/Battle City use an
// OscillatorNode (frequency), the others an AudioBufferSourceNode (playbackRate)
// — mixing them up throws, so a skin change must rebuild the source.
let vroomFlavor: "modern" | "battlecity" | "buffer" | null = null;
export let isVroomPlaying = false;

/** Re-apply the SFX volume to the live engine loop (called when it changes). */
export function refreshVroomGain() {
  if (vroomGainNode) {
    vroomGainNode.gain.value = 0.2 * globalSfxVolume;
  }
}

function currentVroomFlavor(): "modern" | "battlecity" | "buffer" {
  return state.modernEnabled ? "modern" : state.battleCityEnabled ? "battlecity" : "buffer";
}

export function playVroom() {
  if (isVroomPlaying) return;
  vroomFlavor = currentVroomFlavor();

  if (state.modernEnabled) {
    if (audioCtx.state === "suspended") audioCtx.resume();
    vroomSource = audioCtx.createOscillator();
    vroomSource.type = "sine"; // Electric hover/sine whine
    vroomSource.frequency.setValueAtTime(80, audioCtx.currentTime);

    vroomGainNode = audioCtx.createGain();
    vroomGainNode.gain.setValueAtTime(0.08 * globalSfxVolume, audioCtx.currentTime);

    vroomSource.connect(vroomGainNode);
    vroomGainNode.connect(audioCtx.destination);
    vroomSource.start();
    isVroomPlaying = true;
    return;
  }

  if (state.battleCityEnabled) {
    if (audioCtx.state === "suspended") audioCtx.resume();
    vroomSource = audioCtx.createOscillator();
    vroomSource.type = "triangle"; // NES triangle bass engine sound
    vroomSource.frequency.setValueAtTime(55, audioCtx.currentTime);

    vroomGainNode = audioCtx.createGain();
    vroomGainNode.gain.setValueAtTime(0.12 * globalSfxVolume, audioCtx.currentTime);

    vroomSource.connect(vroomGainNode);
    vroomGainNode.connect(audioCtx.destination);
    vroomSource.start();
    isVroomPlaying = true;
    return;
  }

  const buffer = getBuffer("vroom");
  if (!buffer) return;

  if (audioCtx.state === "suspended") audioCtx.resume();
  vroomSource = audioCtx.createBufferSource();
  vroomSource.buffer = buffer;
  vroomSource.loop = true;

  vroomGainNode = audioCtx.createGain();
  // For realistic military skin, let's make it a heavier/lower diesel tank rumble
  const baseVolume = state.realisticEnabled ? 0.20 : 0.12;
  const basePitch = state.realisticEnabled ? 0.60 : 0.75;
  vroomGainNode.gain.value = baseVolume * globalSfxVolume;
  vroomSource.playbackRate.value = basePitch;

  vroomSource.connect(vroomGainNode);
  vroomGainNode.connect(audioCtx.destination);
  vroomSource.start();
  isVroomPlaying = true;
}

export function pauseVroom() {
  if (!isVroomPlaying || !vroomSource) return;
  vroomSource.stop();
  vroomSource.disconnect();
  vroomSource = null;
  vroomGainNode = null;
  vroomFlavor = null;
  isVroomPlaying = false;
}

export function updateVroom(isMoving: boolean, hasBoost: boolean) {
  if (!isVroomPlaying || !vroomSource || !vroomGainNode) return;

  // Skin changed mid-drive: rebuild the source so its node type matches the
  // branch below (and the engine actually sounds like the new skin).
  if (vroomFlavor !== currentVroomFlavor()) {
    pauseVroom();
    playVroom();
    if (!isVroomPlaying || !vroomSource || !vroomGainNode) return;
  }

  if (state.realisticEnabled) {
    let targetPitch = 0.60; // Idle heavy diesel pitch
    let targetGain = 0.20;

    if (isMoving) {
      if (hasBoost) {
        targetPitch = 1.15; // Full-rev heavy tank
        targetGain = 0.35;
      } else {
        targetPitch = 0.85; // Cruising heavy tank
        targetGain = 0.25;
      }
    }

    const now = audioCtx.currentTime;
    vroomSource.playbackRate.setTargetAtTime(targetPitch, now, 0.15);
    vroomGainNode.gain.setTargetAtTime(targetGain * globalSfxVolume, now, 0.15);
    return;
  }

  if (state.modernEnabled) {
    let targetPitch = 70; // Idle electric engine hum
    let targetGain = 0.08;

    if (isMoving) {
      if (hasBoost) {
        targetPitch = 160; // Hyper boost electric hum
        targetGain = 0.22;
      } else {
        targetPitch = 110; // Active hover hum
        targetGain = 0.15;
      }
    }

    const now = audioCtx.currentTime;
    vroomSource.frequency.setTargetAtTime(targetPitch, now, 0.1);
    vroomGainNode.gain.setTargetAtTime(targetGain * globalSfxVolume, now, 0.1);
    return;
  }

  if (state.battleCityEnabled) {
    let targetPitch = 45; // Idle low pitch (Hz)
    let targetGain = 0.12;

    if (isMoving) {
      if (hasBoost) {
        targetPitch = 90; // Speed boost (Hz)
        targetGain = 0.28;
      } else {
        targetPitch = 65; // Moving pitch (Hz)
        targetGain = 0.2;
      }
    }

    const now = audioCtx.currentTime;
    vroomSource.frequency.setTargetAtTime(targetPitch, now, 0.1);
    vroomGainNode.gain.setTargetAtTime(targetGain * globalSfxVolume, now, 0.1);
    return;
  }

  let targetPitch = 0.75; // Quiet low pitch for idle
  let targetGain = 0.12;

  if (isMoving) {
    if (hasBoost) {
      targetPitch = 1.35; // High pitch/rev for speed boost
      targetGain = 0.3;
    } else {
      targetPitch = 1.05; // Standard acceleration pitch
      targetGain = 0.2;
    }
  }

  const now = audioCtx.currentTime;
  vroomSource.playbackRate.setTargetAtTime(targetPitch, now, 0.1);
  vroomGainNode.gain.setTargetAtTime(targetGain * globalSfxVolume, now, 0.1);
}
